/* eslint-disable react/no-danger */
import React, { useContext } from 'react';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import styled from 'styled-components';
import { FullProduct, Shop, SimplePage } from 'data/types';
import { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/router';
import ProductImages from 'components/product/product-images';
import Breadcrumb from '../components/core/breadcrumb';
import { getInitialShop } from '../data/shop';
import Layout from './master-layout';
import { buildImgixUrl } from '../utils/helpers';
import { ContextProps, GlobalContext } from '../context/global-context';
import { mqFrom } from '../styles/styles';
import BuySection from '../components/product/buy-section';
import { getPreviewProduct, getProduct } from '../data/product';
import ProductImagesCarousel from '../components/product/product-images-carousel';
import ArtistDescription from 'components/product/artist-description';
import LoadingPage from './loading-page';
import PageNotFound from './404-page';
import Products from 'components/pageitems/products';
import PreviewModeButton from 'components/core/preview-mode-button';

export type ProductPageProps = {
  shop?: Shop;
  pages?: SimplePage[];
  product: FullProduct;
  preview: boolean;
};

export const ProductPage: React.FC<ProductPageProps> = ({
  product,
  preview,
}) => {
  const { dictionary, device } = useContext<ContextProps>(GlobalContext);
  const router = useRouter();

  if (router.isFallback) {
    return (
      <LoadingPage cssClassName="product" textDictKey="loadingProductPage" />
    );
  }

  if (!product) {
    return <PageNotFound />;
  }

  const {
    name,
    images: rawImages,
    breadcrumb,
    description,
    metaTitle,
    metaDescription,
    metaKeywords,
    metaImage,
    artist,
    subText,
    canonicalUrl,
    relatedProducts,
  } = product;

  const variantSku = router.query?.variantSku as string;
  const images = rawImages.filter((item) => {
    if (
      variantSku &&
      item.sku &&
      item.sku.toLowerCase() !== variantSku.toLowerCase()
    ) {
      return false;
    }
    return true;
  });

  return (
    <Layout cssClassName="product">
      <Head>
        <title>{metaTitle || name}</title>
        <meta name="description" content={metaDescription || ''} />
        <meta name="keywords" content={metaKeywords || ''} />
        <meta property="og:title" content={metaTitle || name} />
        <meta property="og:url" content="test" />
        {metaImage && (
          <meta
            property="og:image"
            content={buildImgixUrl(metaImage.src, {
              w: 1200,
              h: 630,
              fit: 'crop',
              crop: 'faces',
            })}
          />
        )}
        <meta name="twitter:card" content="product" />
        <meta name="twitter:title" content={metaTitle || name} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {metaImage && (
          <meta
            property="twitter:image"
            content={buildImgixUrl(metaImage.src, {
              w: 1200,
              h: 630,
              fit: 'crop',
              crop: 'faces',
            })}
          />
        )}
      </Head>
      {preview && <PreviewModeButton />}
      <Breadcrumb breadcrumb={breadcrumb} />
      <StyledProduct>
        <div className="images">
          {['mobile', 'tablet', 'desktop', 'wide'].includes(device) && (
            <ProductImagesCarousel images={images} variantSku={variantSku} />
          )}
          {['nono'].includes(device) && <ProductImages images={images} />}
        </div>
        <div className="content">
          {artist?.name && <h2 className="artist h3 display">{artist.name}</h2>}
          <h1 className="name">{name}</h1>
          {subText && <h2 className="subtext">{subText}</h2>}

          <BuySection product={product} />

          <ArtistDescription {...artist} />

          <h2 className="light">{dictionary?.productDescription}</h2>
          <div
            className="description"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      </StyledProduct>
      {relatedProducts?.length > 0 && (
        <StyledRelatedProducts className="related-products">
          <h3
            dangerouslySetInnerHTML={{ __html: dictionary.relatedProducts }}
          />
          <Products products={relatedProducts} small rootLink />
        </StyledRelatedProducts>
      )}
    </Layout>
  );
};

const StyledRelatedProducts = styled.section`
  background: #fafafa;
  padding: 0.5rem var(--side-padding);
  margin-top: 2rem;

  > h3 {
    font-weight: 500;
    span {
      font-weight: 300;
    }
    ${mqFrom.desktop} {
      padding: 0 var(--side-padding);
    }
    ${mqFrom.wide} {
      padding: 0 calc(var(--side-padding) * 2) 0 var(--side-padding);
    }
  }
`;

const StyledProduct = styled.section`
  --space-between-lines-margin: 0.3rem 0;

  display: flex;
  flex-direction: column;

  ${mqFrom.desktop} {
    flex-direction: row;
  }

  .images {
    flex: 3 1 0;
    max-width: 1100px;
    ${mqFrom.desktop} {
      padding: 0 var(--side-padding);
    }
    ${mqFrom.wide} {
      padding: 0 calc(var(--side-padding) * 2) 0 var(--side-padding);
    }
  }

  .content {
    max-width: 800px;
    flex: 2 1 0;
    padding: 2rem var(--side-padding);

    ${mqFrom.desktop} {
      padding: 0 var(--side-padding) 0 0;
    }

    .name {
      margin: var(--space-between-lines-margin);
      font-weight: 300;
    }
    .subtext {
      margin: var(--space-between-lines-margin);
      color: var(--base-dimmed-foreground);
      font-weight: 500;
    }
    .artist {
      margin: var(--space-between-lines-margin);
      text-align: left;
      /* color: var(--base-dimmed-foreground); */
    }

    .description {
      img {
        max-width: 100%;
      }
    }

    .model-table {
      max-width: 100%;
    }
  }
`;

interface Params extends ParsedUrlQuery {
  slug1?: string;
  slug2?: string;
  slug3?: string;
  productSlug: string;
  variantSku?: string;
}

// This also gets called at build time
export const getStaticProps: GetStaticProps<ProductPageProps, Params> = async ({
  params,
  locale,
  preview,
  // previewData,
}) => {
  const path = params
    ? ['', params.slug1, params.slug2, params.slug3, params.productSlug]
        .filter((slug) => slug !== undefined)
        .join('/') || '/'
    : '/';

  if (!params.productSlug) {
    console.log('/// getStaticProps product.js not found', {
      locale,
      params,
      path,
    });
    return { notFound: true };
  }

  const { variantSku } = params;
  const { pages, shop } = await getInitialShop(path, locale);

  console.log('@@ product', { path, preview });
  // Handle preview mode
  if (preview) {
    const previewProduct = await getPreviewProduct(
      params.productSlug,
      path,
      locale,
      variantSku || ''
    );

    if (previewProduct._id !== '__404__') {
      return {
        props: {
          shop,
          pages,
          path,
          product: previewProduct,
          preview: true,
        },
        revalidate: 1, // 1 sec
      };
    }
  }

  const product = await getProduct(
    params.productSlug,
    path,
    locale,
    variantSku || ''
  );

  return {
    props: {
      shop,
      pages,
      path,
      product: product._id !== '__404__' ? product : null,
      preview: preview || false,
    },
    revalidate: shop.devMode ? 1 : 60 * 60, // 1 hour
  };
};
